import { useState } from "react";
import { BG_CATALOG, BG_IDS, PERSONA_CATALOG } from "./catalog";

interface Props {
  selected: string[];
  onChange: (next: string[]) => void;
  /** Currently selected personas, used for hover composite preview. */
  personIds: string[];
  /** Section number prefix (e.g. "02"). Default "02". */
  sectionNumber?: string;
  allowEmpty?: boolean;
}

export default function BackgroundPicker({
  selected,
  onChange,
  personIds,
  sectionNumber = "02",
  allowEmpty = true,
}: Props) {
  const [hovered, setHovered] = useState<string | null>(null);
  const previewPersonId = personIds[0] ?? "01_henri";

  const toggle = (id: string) => {
    if (selected.includes(id)) {
      const next = selected.filter((x) => x !== id);
      if (next.length === 0 && !allowEmpty) return;
      onChange(next);
    } else {
      onChange([...selected, id]);
    }
  };

  const selectAll = () => onChange(BG_IDS);
  const clear = () => onChange(allowEmpty ? [] : [BG_IDS[0]]);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h2 className="font-mono text-[10.5px] uppercase text-concrete flex items-center gap-2">
          <span>{sectionNumber}</span>
          <span className="font-sans text-[15px] font-semibold text-charcoal normal-case">
            Fonds
          </span>
        </h2>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10.5px] text-concrete tabular-nums">
            {selected.length}/{BG_CATALOG.length} sélectionné
            {selected.length > 1 ? "s" : ""}
          </span>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={selectAll}
              className="px-2.5 py-1 text-[10.5px] font-mono uppercase border border-charcoal rounded hover:bg-charcoal hover:text-ivory transition"
            >
              Tout
            </button>
            <button
              type="button"
              onClick={clear}
              className="px-2.5 py-1 text-[10.5px] font-mono uppercase border border-stone rounded hover:border-charcoal transition"
            >
              Aucun
            </button>
          </div>
        </div>
      </div>
      <p className="text-xs text-concrete -mt-1">
        Survole un fond pour voir le personnage sélectionné dedans.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {BG_CATALOG.map((b) => {
          const on = selected.includes(b.id);
          const showComposite = hovered === b.id && personIds.length > 0;
          return (
            <button
              key={b.id}
              type="button"
              onClick={() => toggle(b.id)}
              onMouseEnter={() => setHovered(b.id)}
              onMouseLeave={() => setHovered(null)}
              className={`relative text-left rounded-2xl border overflow-hidden transition bg-paper ${
                on
                  ? "border-[2px] border-charcoal bg-ivory-tint shadow-sm"
                  : "border-stone hover:border-charcoal/40"
              }`}
            >
              {on && (
                <span className="absolute top-3 right-3 z-20 w-7 h-7 rounded-full bg-charcoal text-ivory text-sm font-mono flex items-center justify-center shadow">
                  ✓
                </span>
              )}
              <div
                className="w-full aspect-video bg-stone/30 relative"
                style={{
                  backgroundImage: `url(/avatar-assets/backgrounds/${b.id}.jpg)`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                {showComposite && (
                  <>
                    <img
                      src={`/avatar-assets/persons-matted/${previewPersonId}.png`}
                      alt=""
                      loading="lazy"
                      className="absolute inset-y-0 right-0 h-full w-auto object-contain object-bottom"
                    />
                    <span className="absolute bottom-2 left-2 font-mono text-[10px] uppercase tracking-wide bg-charcoal/80 text-ivory px-1.5 py-0.5 rounded">
                      avec {PERSONA_CATALOG.find((x) => x.id === previewPersonId)?.name}
                    </span>
                  </>
                )}
              </div>
              <div className="p-4 flex flex-col gap-2">
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="font-sans text-xl font-semibold leading-tight">
                    {b.name}
                  </h3>
                  <span className="font-mono text-[10.5px] text-concrete shrink-0">
                    {b.family}
                  </span>
                </div>
                <p className="text-sm text-charcoal leading-snug">{b.pitch}</p>
                <div className="mt-1 flex items-center gap-1.5 text-[10.5px] font-mono uppercase tracking-wide text-concrete">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-charcoal/60" />
                  <span>{b.mood}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
